import { CircleCheck, CircleX, Hourglass, LoaderCircle, RefreshCw, RotateCcw } from 'lucide-react'
import type { ReactNode } from 'react'

import { Button, StatusMessage } from '@krp/ui-kit'

import { JOB_ITEM_LABELS, JOB_STATE_LABELS, formatMoment, serverErrorShown } from '../model/labels'
import type { ApplyJob, JobItemState, ShownError } from '../model/types'
import { ErrorBlock } from './parts'
import './sync.css'

export interface JobVm {
  status: 'loading' | 'failed' | 'ready'
  loadError: ShownError | null
  job: ApplyJob | null
  nameOf: (resourceId: string) => string
  /** Повтор неудавшихся строк отправлен и ждёт ответа. */
  retryBusy: boolean
  retryError: ShownError | null
}

export interface JobActions {
  reload: () => void
  /** Повторить только строки с ошибкой — новой задачей. */
  retry: () => void
}

const ITEM_ICONS: Readonly<Record<JobItemState, ReactNode>> = {
  pending: <Hourglass size={14} aria-hidden="true" />,
  running: <LoaderCircle size={14} aria-hidden="true" />,
  succeeded: <CircleCheck size={14} aria-hidden="true" />,
  failed: <CircleX size={14} aria-hidden="true" />,
}

/**
 * Ход фоновой задачи применения: отправлено → идёт → итог по строкам. Ошибка каждой строки — дословно, с кодом, запросом и
 * копированием; ничего не исчезает по таймеру. Окно можно закрыть — задача идёт на сервере, её итог есть в журнале.
 */
export function JobScreen({ vm, actions }: { vm: JobVm; actions: JobActions }) {
  const job = vm.job
  const active = job !== null && (job.state === 'queued' || job.state === 'running')
  return (
    <section aria-labelledby="sync-job-title" className="sync">
      <header>
        <h2 id="sync-job-title">Ход применения</h2>
        {job !== null && (
          <p className="sync__lead">
            Запустил {job.createdByName ?? job.createdBy}, {formatMoment(job.createdAt)}. Окно можно закрыть: задача идёт на сервере, итог
            останется в журнале.
          </p>
        )}
      </header>

      {vm.status === 'loading' && <StatusMessage kind="loading" title="Спрашиваем, как идёт задача…" />}
      {vm.status === 'failed' && vm.loadError !== null && (
        <ErrorBlock
          error={vm.loadError}
          action={
            <Button variant="secondary" icon={<RefreshCw size={16} aria-hidden="true" />} onClick={actions.reload} actionId="sync.job.reload">
              Повторить
            </Button>
          }
        />
      )}
      {job !== null && (
        <>
          <section className="sync__panel" aria-label="Итог">
            <p role="status">
              <span className="sync-badge" data-state={job.state}>
                {active ? ITEM_ICONS.running : job.state === 'completed' ? ITEM_ICONS.succeeded : ITEM_ICONS.failed}
                {JOB_STATE_LABELS[job.state]}
              </span>{' '}
              Применено {job.done} из {job.total}
              {job.failed > 0 && `, с ошибкой ${job.failed}`}
              {job.finishedAt !== null && ` · закончено ${formatMoment(job.finishedAt)}`}
            </p>
            <progress className="sync-progress" max={Math.max(job.total, 1)} value={job.done + job.failed} aria-label="Ход задачи" />
            {job.errorMessage !== null && <ErrorBlock error={serverErrorShown('Задача остановилась', job.errorMessage, null, null)} />}
            {!active && job.failed > 0 && (
              <div className="sync__row">
                <p className="sync__note">
                  Строки с ошибкой остались в списке расхождений. Исправьте причину и повторите только их: строку, исход которой неизвестен,
                  служба сначала проверит по прежнему ключу — второй записи не будет.
                </p>
                <Button
                  variant="secondary"
                  icon={<RotateCcw size={16} aria-hidden="true" />}
                  busy={vm.retryBusy}
                  disabled={vm.retryBusy}
                  disabledReason={vm.retryBusy ? 'Повтор уже отправлен.' : null}
                  onClick={actions.retry}
                  actionId="sync.job.retry"
                >
                  Повторить неудавшиеся ({job.failed})
                </Button>
              </div>
            )}
            {vm.retryError !== null && <ErrorBlock error={vm.retryError} />}
          </section>
          {job.items.length > 0 && (
            <ul className="sync-job-items" aria-label="Строки задачи">
              {job.items.map((item) => (
                <li key={item.id} className="sync__panel">
                  <div className="sync__row">
                    <span className="sync-badge" data-state={item.state}>
                      {ITEM_ICONS[item.state]}
                      {JOB_ITEM_LABELS[item.state]}
                    </span>
                    <span className="sync-item__name">{item.title}</span>
                    <span className="sync__note">→ {vm.nameOf(item.targetResourceId)}</span>
                  </div>
                  {item.state === 'failed' && item.message !== null && (
                    <ErrorBlock error={serverErrorShown(`${item.title} → ${vm.nameOf(item.targetResourceId)}`, item.message, item.errorCode, item.requestId)} />
                  )}
                  {item.state === 'succeeded' && item.message !== null && <p className="sync__note">{item.message}</p>}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  )
}
